/**
 * Service Élèves / Espace parent - API Tracking
 * Endpoints: my-students, students/<id>/profile, active-trips, trip-history, payments,
 * presence-history, statistics, student-availability, parent payments
 */
import { createTrackingClient } from "../trackingApi";

export interface MyStudent {
  id: number;
  student_uuid?: string;
  user_id?: number | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  code_identifier?: string | null;
  profile_picture?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  address_label?: string | null;
  payment_status?: string;
  payment_due?: number | null;
  institution_id?: number | null;
  institution_name?: string | null;
  sub_area_id?: number | null;
  sub_area_name?: string | null;
  parent_id?: string | null;
  parent_name?: string | null;
  is_available?: boolean;
  has_active_trip?: boolean;
  unavailability_history?: UnavailabilityRecord[];
}

export interface UnavailabilityRecord {
  id: string;
  reason?: string;
  start_date: string;
  end_date: string | null;
  created_at: string | null;
}

export interface StudentProfileUpdatePayload {
  first_name?: string;
  last_name?: string;
  latitude?: number | null;
  longitude?: number | null;
  address_label?: string | null;
  profile_picture?: File | null;
}

export interface StudentAvailabilityPayload {
  student_id: number;
  is_available: boolean;
  reason?: string;
  start_date?: string;
  end_date?: string;
}

export interface StudentActiveTrip {
  id: string;
  bus?: { name?: string; plate_number?: string };
  route?: { name?: string };
  status?: string;
  started_at?: string | null;
  finished_at?: string | null;
  driver_username?: string;
  [key: string]: unknown;
}

export interface StudentTripHistoryItem {
  id: string;
  bus?: { name?: string };
  route?: { name?: string };
  status?: string;
  started_at?: string | null;
  finished_at?: string | null;
  [key: string]: unknown;
}

export interface StudentPayment {
  id: string;
  amount?: number | string;
  status?: string;
  paid_at?: string;
  invoice?: { id?: string };
  [key: string]: unknown;
}

export interface StudentPresenceItem {
  id?: string;
  date?: string;
  present?: boolean;
  trip_id?: string;
  [key: string]: unknown;
}

export interface StudentStatistics {
  total_trips?: number;
  total_present?: number;
  total_absent?: number;
  [key: string]: unknown;
}

// --- Administration des élèves (création unitaire + import CSV) ---

export interface StudentCreatePayload {
  institution_id: number;
  first_name: string;
  last_name: string;
  username?: string;
  email?: string;
  parent_email?: string;
  address_label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface AdminStudentsListResponse {
  count: number;
  next: number | null;
  previous: number | null;
  results: MyStudent[];
}

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

/** Liste des élèves du parent connecté */
export async function fetchMyStudents(
  accessToken: string | undefined
): Promise<MyStudent[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<MyStudent[]>("/api/users/my-students/");
  return Array.isArray(data) ? data : [];
}

/** Mise à jour du profil élève (PUT/PATCH) */
export async function updateStudentProfile(
  accessToken: string | undefined,
  studentId: number,
  payload: Partial<StudentProfileUpdatePayload>,
  method: "PUT" | "PATCH" = "PATCH"
): Promise<MyStudent> {
  const client = getClient(accessToken);
  const formData = new FormData();
  if (payload.first_name !== undefined) formData.append("first_name", payload.first_name);
  if (payload.last_name !== undefined) formData.append("last_name", payload.last_name);
  if (payload.address_label !== undefined) formData.append("address_label", payload.address_label ?? "");
  if (payload.latitude !== undefined && payload.latitude !== null) formData.append("latitude", String(payload.latitude));
  if (payload.longitude !== undefined && payload.longitude !== null) formData.append("longitude", String(payload.longitude));
  if (payload.profile_picture instanceof File) formData.append("profile_picture", payload.profile_picture);
  const config = { headers: { "Content-Type": "multipart/form-data" } };
  if (method === "PUT") {
    const { data } = await client.put<MyStudent>(`/api/users/students/${studentId}/profile/`, formData, config);
    return data;
  }
  const { data } = await client.patch<MyStudent>(`/api/users/students/${studentId}/profile/`, formData, config);
  return data;
}

/** Trajets actifs d'un élève */
export async function fetchStudentActiveTrips(
  accessToken: string | undefined,
  studentId: number
): Promise<StudentActiveTrip[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<StudentActiveTrip[]>(
    `/api/users/students/${studentId}/active-trips/`
  );
  return Array.isArray(data) ? data : [];
}

/** Historique des trajets d'un élève */
export async function fetchStudentTripHistory(
  accessToken: string | undefined,
  studentId: number,
  params?: { page?: number; page_size?: number }
): Promise<{ count: number; results: StudentTripHistoryItem[] }> {
  const client = getClient(accessToken);
  const { data } = await client.get<{ count: number; results: StudentTripHistoryItem[] }>(
    `/api/users/students/${studentId}/trip-history/`,
    { params }
  );
  return {
    count: data?.count ?? 0,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/** Paiements d'un élève */
export async function fetchStudentPayments(
  accessToken: string | undefined,
  studentId: number,
  params?: { page?: number; page_size?: number }
): Promise<{ count: number; results: StudentPayment[] }> {
  const client = getClient(accessToken);
  const { data } = await client.get<{ count: number; results: StudentPayment[] }>(
    `/api/users/students/${studentId}/payments/`,
    { params }
  );
  return {
    count: data?.count ?? 0,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/** Historique de présence d'un élève */
export async function fetchStudentPresenceHistory(
  accessToken: string | undefined,
  studentId: number,
  params?: { page?: number; page_size?: number; start_date?: string; end_date?: string }
): Promise<{ count: number; results: StudentPresenceItem[] }> {
  const client = getClient(accessToken);
  const { data } = await client.get<{ count: number; results: StudentPresenceItem[] }>(
    `/api/users/students/${studentId}/presence-history/`,
    { params }
  );
  return {
    count: data?.count ?? 0,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/** Statistiques d'un élève */
export async function fetchStudentStatistics(
  accessToken: string | undefined,
  studentId: number
): Promise<StudentStatistics> {
  const client = getClient(accessToken);
  const { data } = await client.get<StudentStatistics>(
    `/api/users/students/${studentId}/statistics/`
  );
  return data ?? {};
}

/** Mise à jour disponibilité élève */
export async function updateStudentAvailability(
  accessToken: string | undefined,
  payload: StudentAvailabilityPayload
): Promise<{ student_id: number; is_available: boolean; history_record_id?: string }> {
  const client = getClient(accessToken);
  const { data } = await client.post("/api/users/student-availability/", payload);
  return data;
}

/** Paiements du parent (tous élèves) */
export async function fetchParentPayments(
  accessToken: string | undefined,
  params?: { page?: number; page_size?: number }
): Promise<{ count: number; results: StudentPayment[] }> {
  const client = getClient(accessToken);
  const response = await client.get<{ count: number; results: StudentPayment[] }>(
    "/api/users/payments/",
    { params }
  );
  const data = response.data;
  return {
    count: data?.count ?? 0,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/**
 * Liste des élèves (vue admin/établissement) — pagination.
 * GET /api/v1/students/?page=&page_size=
 */
export async function fetchAdminStudents(
  accessToken: string | undefined,
  params?: { page?: number; page_size?: number }
): Promise<AdminStudentsListResponse> {
  const client = getClient(accessToken);
  const { data } = await client.get<AdminStudentsListResponse>("/api/v1/students/", {
    params,
  });
  return {
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/**
 * Création unitaire d'un élève (administration).
 * 
 * NOTE : l'endpoint est aligné sur le document d'impact Tracking et devra
 * être implémenté côté API si ce n'est pas déjà le cas.
 */
export async function createStudent(
  accessToken: string | undefined,
  payload: StudentCreatePayload
): Promise<MyStudent> {
  const client = getClient(accessToken);
  const { data } = await client.post<MyStudent>("/api/v1/students/", payload);
  return data;
}

/**
 * Import CSV d'élèves (création en masse).
 * 
 * Le backend est censé accepter un FormData avec :
 * - institution_id
 * - csv_file
 */
export async function uploadStudentsCsv(
  accessToken: string | undefined,
  formData: FormData
): Promise<any> {
  const client = getClient(accessToken);
  const { data } = await client.post("/api/v1/students/upload/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
